import { type ReactNode } from 'react';
import { cn } from '@/nucleo/utilitarios';
import { DOC_NAME, DOC_VERSION, numeroDaSecao } from './printDoc';

/**
 * Uma folha A4 do documento: uma folha é uma página impressa.
 *
 * Na primeira folha de uma seção vai o cabeçalho completo (número, título e
 * frase de abertura); nas de continuação, uma linha só com número e título.
 * Sem `secao`, a folha não tem cabeçalho (o sumário traz o próprio título).
 * O rodapé leva o nome do documento, a versão e `pagina/total`.
 */
export function Sheet({
  children,
  pagina,
  total,
  secao,
  continuacao = false,
}: {
  children?: ReactNode;
  pagina: number;
  total: number;
  secao?: { numero: number; titulo: string; intro?: string };
  continuacao?: boolean;
}) {
  return (
    <section className="ds-sheet" data-pagina={pagina}>
      <div className="ds-sheet-guides" aria-hidden="true" />

      {secao && !continuacao && (
        <header className="flex items-baseline gap-3 border-b border-border pb-3 mb-5">
          <span className="text-3xl font-extrabold font-mono text-primary leading-none">
            {numeroDaSecao(secao.numero)}
          </span>
          <div className="min-w-0">
            <h2 className="text-xl font-bold tracking-tight text-foreground">{secao.titulo}</h2>
            {secao.intro && <p className="text-xs text-muted-foreground mt-0.5">{secao.intro}</p>}
          </div>
        </header>
      )}
      {secao && continuacao && (
        <header className="flex items-baseline gap-2 border-b border-border pb-2 mb-4 text-xs text-muted-foreground">
          <span className="font-mono font-semibold text-primary">
            {numeroDaSecao(secao.numero)}
          </span>
          <span className="font-medium">{secao.titulo}</span>
          <span>(continuação)</span>
        </header>
      )}

      <div className="ds-sheet-body">{children}</div>

      <footer className="ds-sheet-footer">
        <span>
          {DOC_NAME} · {DOC_VERSION}
        </span>
        <span className="font-mono">
          {pagina}/{total}
        </span>
      </footer>
    </section>
  );
}

/**
 * Bloco que abre folha nova no documento impresso, mesmo que caiba na folha
 * corrente (páginas de tema; plano B da paginação, com folhas declaradas à
 * mão). Na tela, não muda nada.
 */
export function PaginaPropria({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

export function Block({
  title,
  children,
  className,
}: {
  title?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('mb-5 ds-content-block', className)}>
      {title && (
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
          {title}
        </h3>
      )}
      {children}
    </div>
  );
}

export function Para({ children }: { children: ReactNode }) {
  return <p className="text-sm text-muted-foreground leading-relaxed mb-3">{children}</p>;
}

export function Bullets({ items }: { items: string[] }) {
  return (
    <ul className="space-y-1.5 mb-3">
      {items.map((it) => (
        <li key={it} className="flex gap-2 text-sm text-muted-foreground">
          <span className="mt-[7px] h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

export function Table({ head, rows }: { head: string[]; rows: (string | ReactNode)[][] }) {
  return (
    <div className="overflow-x-auto ds-table-wrap">
      <table className="w-full text-xs border border-border rounded-lg overflow-hidden">
        <thead>
          <tr className="bg-muted">
            {head.map((h) => (
              <th key={h} className="text-left px-3 py-2 font-semibold text-foreground">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-border">
              {r.map((c, j) => (
                <td key={j} className="px-3 py-1.5 text-muted-foreground align-top">
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
